import {defineMessages} from 'react-intl';
import {filter, take} from 'rxjs/operators';
import {useEffect} from 'react';

import {Conversation} from '@stripe-internal/conversations-client';
import {ViewMode} from '@stripe-internal/support-conversations-controller';

import {isDotComConversation} from '../../core/lib/util/conversationUtils';
import {useViewMode} from '../../core/hooks/appHooks';

export const messages = defineMessages({
  chatWithAgentEnded: {
    id: 'conversations_assistant.conversationLog.chatWithAgentEndedMessage',
    description:
      'Message to show to prompt user to close chat widget to start new chat',
    defaultMessage:
      '{name} has left the chat. Ask a new question by typing below.',
  },
  conversationEnded: {
    id: 'conversations_assistant.conversationLog.conversationEnded',
    description:
      'Message to show to prompt user to close chat widget to start new chat (in the drawer)',
    defaultMessage:
      'You can now close this drawer or ask a new question by typing below.',
  },
  conversationEndedForWidget: {
    id: 'conversations_assistant.conversationLog.conversationEndedForWidget',
    description:
      'Message to show to prompt user to close chat widget to start new chat (in the widget)',
    defaultMessage:
      'The chat has ended. For further assistance, close the window and start a new chat.',
  },
  racEnded: {
    id: 'conversations_assistant.conversationLog.racEnded',
    description:
      'Message to show to prompt user to close chat widget to start new chat',
    defaultMessage:
      'Call completed. You can now close this drawer or ask a new question by typing below.',
  },
  racCanceled: {
    id: 'conversations_assistant.conversationLog.racCanceled',
    description:
      'Message to show to prompt user to close chat widget to start new chat due to cancellation',
    defaultMessage:
      'Call canceled. You can now close this drawer or ask Assistant a new question by typing below.',
  },
  racEndedForWidget: {
    id: 'conversations_assistant.conversationLog.racEndedForWidget',
    description:
      'Message to show to prompt user to close chat widget to start new chat (in the widget)',
    defaultMessage:
      'Call received. For further assistance, close the window and start a new chat.',
  },
  racCanceledForWidget: {
    id: 'conversations_assistant.conversationLog.racCanceledForWidget',
    description:
      'Message to show to prompt user to close chat widget to start new chat due to cancellation (in the widget)',
    defaultMessage:
      'Call canceled. For further assistance, close the window and start a new chat.',
  },
});

function getAgentName(conversation: Conversation): string | undefined {
  return Object.values(conversation.participants).find(
    (p) => p.role === 'agent',
  )?.displayName;
}

function getMessageForConversation(
  conversation: Conversation,
  drawerEnabled: boolean,
) {
  const {channel} = conversation.context;
  const agentName = getAgentName(conversation);

  if (channel === 'request_a_call') {
    if (conversation.context.requestACallDetails?.callCanceled) {
      return {
        message: drawerEnabled
          ? messages.racCanceled
          : messages.racCanceledForWidget,
      };
    }
    return {
      message: drawerEnabled ? messages.racEnded : messages.racEndedForWidget,
    };
  } else if (agentName && channel === 'chat') {
    return {message: messages.chatWithAgentEnded, values: {name: agentName}};
  }

  return {
    message: drawerEnabled
      ? messages.conversationEnded
      : messages.conversationEndedForWidget,
  };
}

export function useConversationLogEvents(conversation: Conversation) {
  const viewMode = useViewMode();
  const drawerEnabled = viewMode === ViewMode.Drawer;

  useEffect(() => {
    const sub = conversation
      .eventsObservable()
      .pipe(
        filter((e) => e.name === 'conversation-ended'),
        take(1),
      )
      .subscribe(() => {
        // For dot_com entry points, let UHLiveChannelsClient handle when to show the chat ended message.
        // It'll show after animations are complete.
        if (isDotComConversation(conversation)) {
          return;
        }

        const {message, values} = getMessageForConversation(
          conversation,
          drawerEnabled,
        );
        if (!conversation.context.supressChatEndedNotice) {
          conversation.log.appendMetaEvent({
            id: 'chat-ended-notice',
            message,
            values,
          });
        }
      });

    return () => sub.unsubscribe();
  }, [conversation, drawerEnabled]);
}
